import { useState } from "react";
import {
  ArrowRight,
  ArrowUpRight,
  CheckCircle2,
  Clock3,
  Mail,
  MapPin,
  MessageSquare,
  Phone,
  Send,
  ClipboardList,
} from "lucide-react";

const initialFormData = {
  name: "",
  email: "",
  phone: "",
  projectType: "",
  location: "",
  budget: "",
  message: "",
  source: "",
};

const projectTypes = [
  "Residential Interior Design",
  "Commercial Interior Design",
  "New Home Construction",
  "Complete Turnkey Project",
  "Modular Kitchen & Wardrobes",
  "Interior Painting",
  "Exterior Painting",
  "Waterproofing & Finishing",
  "Other",
];

const budgets = [
  "Below ₹2 Lakhs",
  "₹2–5 Lakhs",
  "₹5–10 Lakhs",
  "₹10–20 Lakhs",
  "₹20–35 Lakhs",
  "₹35 Lakhs and Above",
  "Need Guidance",
];

const inputClass =
  "w-full min-w-0 rounded-xl border border-slate-200 bg-white px-4 py-3.5 font-['DM_Sans',sans-serif] text-sm text-slate-900 outline-none transition placeholder:text-slate-400 hover:border-slate-300 focus:border-indigo-500 focus:ring-4 focus:ring-indigo-500/10 sm:py-4";

const labelClass =
  "mb-2 block font-['DM_Sans',sans-serif] text-sm font-semibold text-slate-700";

export default function Contact() {
  const [formData, setFormData] = useState(initialFormData);
  const [submitted, setSubmitted] = useState(false);

  const handleChange = (event) => {
    const { name, value } = event.target;

    setFormData((previous) => ({
      ...previous,
      [name]: value,
    }));

    if (submitted) setSubmitted(false);
  };

  const handleSubmit = (event) => {
    event.preventDefault();

    // Connect this handler to your backend or form service
    // to send enquiries to your business.
    console.log("Contact enquiry:", formData);
    setSubmitted(true);
  };

  return (
    <section
      id="contact"
      className="relative w-full overflow-hidden bg-[#f7f8fc] py-14 sm:py-16 md:py-20 lg:py-24"
    >
      {/* Background accents */}
      <div
        aria-hidden="true"
        className="pointer-events-none absolute -right-32 top-0 h-72 w-72 rounded-full bg-indigo-200/30 blur-3xl sm:h-96 sm:w-96"
      />

      <div
        aria-hidden="true"
        className="pointer-events-none absolute -bottom-20 -left-24 h-72 w-72 rounded-full bg-blue-100/40 blur-3xl"
      />

      <div className="relative mx-auto w-full max-w-[1400px] px-4 sm:px-6 md:px-8 lg:px-10 xl:px-12">
        {/* Header */}
        <header className="mb-10 grid gap-6 sm:mb-12 md:mb-14 lg:grid-cols-[1.1fr_0.75fr] lg:items-end lg:gap-12">
          <div>
            <div className="mb-5 inline-flex items-center gap-2 rounded-full border border-indigo-100 bg-white px-4 py-2 shadow-sm">
              <MessageSquare size={15} className="text-indigo-600" />

              <span className="font-['DM_Sans',sans-serif] text-[11px] font-bold uppercase tracking-[0.16em] text-indigo-700 sm:text-xs">
                Let’s Build Something Better
              </span>
            </div>

            <h2 className="max-w-2xl font-['Poppins',sans-serif] text-3xl font-semibold leading-[1.2] tracking-tight text-slate-950 sm:text-4xl md:text-5xl lg:text-[3.4rem]">
              Your ideas deserve
              <span className="mt-1 block text-indigo-600">
                a space of their own.
              </span>
            </h2>
          </div>

          <div className="lg:pb-1">
            <p className="font-['DM_Sans',sans-serif] text-sm leading-7 text-slate-600 sm:text-base">
              Planning a new home, transforming an existing space, or starting
              a commercial project? Tell us what you have in mind. We’ll help
              you explore the next steps.
            </p>

            <div className="mt-5 flex flex-wrap gap-2">
              {["Interior Design", "Construction", "Painting"].map((item) => (
                <span
                  key={item}
                  className="rounded-full border border-slate-200 bg-white px-3 py-1.5 font-['DM_Sans',sans-serif] text-xs font-medium text-slate-600"
                >
                  {item}
                </span>
              ))}
            </div>
          </div>
        </header>

        {/* Main layout */}
        <div className="grid min-w-0 grid-cols-1 items-start gap-5 md:gap-6 lg:grid-cols-[1.25fr_0.75fr] lg:gap-7">
          {/* Contact form */}
          <div className="min-w-0 rounded-2xl border border-slate-200/80 bg-white p-5 shadow-[0_8px_40px_rgba(15,23,42,0.035)] sm:rounded-3xl sm:p-7 md:p-9 lg:p-10">
            <div className="mb-7 border-b border-slate-100 pb-6 sm:mb-8 sm:pb-7">
              <span className="font-['DM_Sans',sans-serif] text-xs font-bold uppercase tracking-[0.16em] text-indigo-600">
                Project Enquiry
              </span>

              <h3 className="mt-2 font-['Poppins',sans-serif] text-2xl font-semibold tracking-tight text-slate-950 sm:text-3xl">
                Tell us about your project
              </h3>

              <p className="mt-2 max-w-lg font-['DM_Sans',sans-serif] text-sm leading-6 text-slate-500">
                Share a few details so we can better understand your space,
                priorities, and requirements.
              </p>
            </div>

            <form onSubmit={handleSubmit} className="space-y-5 sm:space-y-6">
              {/* Name and email */}
              <div className="grid grid-cols-1 gap-5 sm:grid-cols-2 sm:gap-4">
                <div className="min-w-0">
                  <label htmlFor="name" className={labelClass}>
                    Full Name <span className="text-indigo-600">*</span>
                  </label>

                  <input
                    id="name"
                    name="name"
                    type="text"
                    autoComplete="name"
                    value={formData.name}
                    onChange={handleChange}
                    placeholder="Enter your name"
                    className={inputClass}
                    required
                  />
                </div>

                <div className="min-w-0">
                  <label htmlFor="email" className={labelClass}>
                    Email Address <span className="text-indigo-600">*</span>
                  </label>

                  <input
                    id="email"
                    name="email"
                    type="email"
                    autoComplete="email"
                    value={formData.email}
                    onChange={handleChange}
                    placeholder="you@example.com"
                    className={inputClass}
                    required
                  />
                </div>
              </div>

              {/* Phone and project type */}
              <div className="grid grid-cols-1 gap-5 sm:grid-cols-2 sm:gap-4">
                <div className="min-w-0">
                  <label htmlFor="phone" className={labelClass}>
                    Phone Number <span className="text-indigo-600">*</span>
                  </label>

                  <input
                    id="phone"
                    name="phone"
                    type="tel"
                    autoComplete="tel"
                    value={formData.phone}
                    onChange={handleChange}
                    placeholder="+91 XXXXX XXXXX"
                    className={inputClass}
                    required
                  />
                </div>

                <div className="min-w-0">
                  <label htmlFor="projectType" className={labelClass}>
                    Project Type <span className="text-indigo-600">*</span>
                  </label>

                  <select
                    id="projectType"
                    name="projectType"
                    value={formData.projectType}
                    onChange={handleChange}
                    className={`${inputClass} appearance-none`}
                    required
                  >
                    <option value="">Choose a service</option>
                    {projectTypes.map((type) => (
                      <option key={type} value={type}>
                        {type}
                      </option>
                    ))}
                  </select>
                </div>
              </div>

              {/* Location and budget */}
              <div className="grid grid-cols-1 gap-5 sm:grid-cols-2 sm:gap-4">
                <div className="min-w-0">
                  <label htmlFor="location" className={labelClass}>
                    Project Location <span className="text-indigo-600">*</span>
                  </label>

                  <input
                    id="location"
                    name="location"
                    type="text"
                    autoComplete="address-level2"
                    value={formData.location}
                    onChange={handleChange}
                    placeholder="City or area"
                    className={inputClass}
                    required
                  />
                </div>

                <div className="min-w-0">
                  <label htmlFor="budget" className={labelClass}>
                    Estimated Budget
                  </label>

                  <select
                    id="budget"
                    name="budget"
                    value={formData.budget}
                    onChange={handleChange}
                    className={`${inputClass} appearance-none`}
                  >
                    <option value="">Choose a budget</option>
                    {budgets.map((budget) => (
                      <option key={budget} value={budget}>
                        {budget}
                      </option>
                    ))}
                  </select>
                </div>
              </div>

              {/* Project description */}
              <div>
                <label htmlFor="message" className={labelClass}>
                  Project Details <span className="text-indigo-600">*</span>
                </label>

                <textarea
                  id="message"
                  name="message"
                  value={formData.message}
                  onChange={handleChange}
                  rows={4}
                  placeholder="Describe your requirements, preferred style, approximate size, or timeline..."
                  className={`${inputClass} min-h-28 resize-y leading-6`}
                  required
                />
              </div>

              {/* Discovery source */}
              <div>
                <label htmlFor="source" className={labelClass}>
                  How did you find us?
                </label>

                <select
                  id="source"
                  name="source"
                  value={formData.source}
                  onChange={handleChange}
                  className={`${inputClass} appearance-none`}
                >
                  <option value="">Select an option (optional)</option>
                  <option value="Google Search">Google Search</option>
                  <option value="Instagram">Instagram</option>
                  <option value="Facebook">Facebook</option>
                  <option value="Referral">Friend or Family</option>
                  <option value="Other">Other</option>
                </select>
              </div>

              {/* Submission feedback */}
              {submitted && (
                <div
                  role="status"
                  className="flex items-start gap-3 rounded-xl border border-emerald-200 bg-emerald-50 p-4"
                >
                  <CheckCircle2
                    size={19}
                    className="mt-0.5 shrink-0 text-emerald-600"
                  />

                  <p className="font-['DM_Sans',sans-serif] text-sm leading-6 text-emerald-800">
                    Your form was validated. Connect the submit handler to
                    your email service or backend to deliver this enquiry.
                  </p>
                </div>
              )}

              {/* Submit */}
              <div className="pt-1">
                <button
                  type="submit"
                  className="group flex w-full items-center justify-center gap-3 rounded-xl bg-indigo-600 px-6 py-4 font-['DM_Sans',sans-serif] text-sm font-bold text-white shadow-lg shadow-indigo-600/15 transition hover:bg-indigo-700 focus:outline-none focus:ring-4 focus:ring-indigo-500/20 active:scale-[0.99] sm:py-[18px]"
                >
                  Send Project Enquiry

                  <ArrowRight
                    size={18}
                    className="transition-transform duration-200 group-hover:translate-x-1"
                  />
                </button>

                <p className="mt-4 text-center font-['DM_Sans',sans-serif] text-xs leading-5 text-slate-400">
                  Your project details help us understand your requirements.
                </p>
              </div>
            </form>
          </div>

          {/* Contact information */}
          <aside className="flex min-w-0 flex-col gap-5 sm:gap-6">
            <div className="relative overflow-hidden rounded-2xl bg-slate-950 p-6 text-white sm:rounded-3xl sm:p-8 lg:p-8 xl:p-9">
              <div
                aria-hidden="true"
                className="pointer-events-none absolute -right-20 -top-20 h-56 w-56 rounded-full bg-indigo-500/20 blur-3xl"
              />

              <div className="relative">
                <span className="font-['DM_Sans',sans-serif] text-xs font-bold uppercase tracking-[0.17em] text-indigo-300">
                  Direct Contact
                </span>

                <h3 className="mt-3 font-['Poppins',sans-serif] text-2xl font-semibold leading-snug sm:text-3xl">
                  Let’s start a conversation.
                </h3>

                <p className="mt-3 font-['DM_Sans',sans-serif] text-sm leading-7 text-slate-400">
                  Have questions before submitting your enquiry? Reach out
                  through the contact details below.
                </p>

                <div className="mt-8 space-y-6">
                  {/* Phone */}
                  <div className="flex min-w-0 gap-4">
                    <div className="flex h-11 w-11 shrink-0 items-center justify-center rounded-xl border border-white/10 bg-white/[0.06] text-indigo-300">
                      <Phone size={19} />
                    </div>

                    <div className="min-w-0 pt-0.5">
                      <p className="font-['DM_Sans',sans-serif] text-xs font-medium text-slate-400">
                        Phone
                      </p>

                      <a
                        href="tel:+919876543210"
                        className="mt-1 block break-words font-['DM_Sans',sans-serif] text-sm font-semibold text-white hover:text-indigo-300 sm:text-base"
                      >
                        +91 98765 43210
                      </a>

                      <a
                        href="tel:+918765432109"
                        className="mt-1 block break-words font-['DM_Sans',sans-serif] text-sm font-semibold text-white hover:text-indigo-300 sm:text-base"
                      >
                        +91 87654 32109
                      </a>
                    </div>
                  </div>

                  {/* Email */}
                  <div className="flex min-w-0 gap-4">
                    <div className="flex h-11 w-11 shrink-0 items-center justify-center rounded-xl border border-white/10 bg-white/[0.06] text-indigo-300">
                      <Mail size={19} />
                    </div>

                    <div className="min-w-0 pt-0.5">
                      <p className="font-['DM_Sans',sans-serif] text-xs font-medium text-slate-400">
                        Email & Website
                      </p>

                      <a
                        href="mailto:cubefourspaces@gmail.com"
                        className="mt-1 block break-all font-['DM_Sans',sans-serif] text-sm font-semibold text-white hover:text-indigo-300"
                      >
                        cubefourspaces@gmail.com
                      </a>

                      <a
                        href="https://www.cube4spaces.in/"
                        target="_blank"
                        rel="noopener noreferrer"
                        className="mt-1 inline-flex max-w-full items-center gap-1 break-all font-['DM_Sans',sans-serif] text-sm text-indigo-300 transition hover:text-white"
                      >
                        cube4spaces.in
                        <ArrowUpRight size={14} className="shrink-0" />
                      </a>
                    </div>
                  </div>

                  {/* Office hours */}
                  <div className="flex min-w-0 gap-4">
                    <div className="flex h-11 w-11 shrink-0 items-center justify-center rounded-xl border border-white/10 bg-white/[0.06] text-indigo-300">
                      <Clock3 size={19} />
                    </div>

                    <div className="min-w-0 pt-0.5">
                      <p className="font-['DM_Sans',sans-serif] text-xs font-medium text-slate-400">
                        Office Hours
                      </p>

                      <p className="mt-1 font-['DM_Sans',sans-serif] text-sm font-semibold text-white">
                        Monday – Saturday
                      </p>

                      <p className="mt-1 font-['DM_Sans',sans-serif] text-sm text-slate-400">
                        9:00 AM – 7:00 PM
                      </p>
                    </div>
                  </div>

                  {/* Address */}
                  <div className="flex min-w-0 gap-4">
                    <div className="flex h-11 w-11 shrink-0 items-center justify-center rounded-xl border border-white/10 bg-white/[0.06] text-indigo-300">
                      <MapPin size={19} />
                    </div>

                    <div className="min-w-0 pt-0.5">
                      <p className="font-['DM_Sans',sans-serif] text-xs font-medium text-slate-400">
                        Studio Address
                      </p>

                      <p className="mt-1 font-['DM_Sans',sans-serif] text-sm leading-6 text-white">
                        #15, 5th Cross, Race Course Road,
                        <br />
                        Opposite to Railway Station,
                        <br />
                        Coimbatore – 641018,
                        <br />
                        Tamil Nadu, India.
                      </p>
                    </div>
                  </div>
                </div>

                <div className="mt-8 border-t border-white/10 pt-6">
                  <p className="font-['DM_Sans',sans-serif] text-xs leading-6 text-slate-400">
                    Interior design, construction, painting, and finishing
                    solutions for residential and commercial spaces.
                  </p>
                </div>
              </div>
            </div>

            {/* Helpful information card */}
            <div className="rounded-2xl border border-slate-200 bg-white p-6 sm:rounded-3xl sm:p-7">
              <div className="flex items-start gap-4">
                <div className="flex h-11 w-11 shrink-0 items-center justify-center rounded-xl bg-indigo-50 text-indigo-600">
                  <ClipboardList size={20} />
                </div>

                <div>
                  <h4 className="font-['Poppins',sans-serif] text-base font-semibold text-slate-900">
                    Not sure where to begin?
                  </h4>

                  <p className="mt-2 font-['DM_Sans',sans-serif] text-sm leading-6 text-slate-500">
                    Start with your ideas, space requirements, and budget.
                    These details can help guide your initial project
                    discussion.
                  </p>
                </div>
              </div>
            </div>
          </aside>
        </div>

        {/* Google Maps */}
        <div className="mt-6 overflow-hidden rounded-2xl border border-slate-200 bg-white p-4 shadow-sm sm:mt-7 sm:rounded-3xl sm:p-6 md:p-8">
          <div className="mb-5 flex flex-col gap-4 sm:mb-6 sm:flex-row sm:items-end sm:justify-between">
            <div>
              <span className="font-['DM_Sans',sans-serif] text-xs font-bold uppercase tracking-[0.16em] text-indigo-600">
                Visit Our Studio
              </span>

              <h3 className="mt-2 font-['Poppins',sans-serif] text-2xl font-semibold tracking-tight text-slate-950 sm:text-3xl">
                Find us in Coimbatore
              </h3>

              <p className="mt-2 font-['DM_Sans',sans-serif] text-sm leading-6 text-slate-500">
                Explore our studio location on Google Maps.
              </p>
            </div>

            <a
              href="https://maps.google.com/?q=Cube+4+spaces"
              target="_blank"
              rel="noopener noreferrer"
              className="inline-flex w-full items-center justify-center gap-2 rounded-xl border border-slate-200 px-4 py-3 font-['DM_Sans',sans-serif] text-sm font-semibold text-slate-700 transition hover:border-indigo-200 hover:bg-indigo-50 hover:text-indigo-700 sm:w-auto"
            >
              Open Google Maps
              <ArrowUpRight size={16} />
            </a>
          </div>

          <div className="h-[260px] overflow-hidden rounded-xl bg-slate-100 sm:h-[340px] md:h-[400px]">
            <iframe
              src="https://www.google.com/maps/embed?pb=!1m18!1m12!1m3!1d16593.456704703814!2d77.00345953278574!3d11.073477335096785!2m3!1f0!2f0!3f0!3m2!1i1024!2i768!4f13.1!3m3!1m2!1s0x3ba8f7d22a2e74b7%3A0x1cf0c55fe95cc923!2sCube%204%20spaces!5e0!3m2!1sen!2sin!4v1790678432943!5m2!1sen!2sin"
              title="Cube 4 Spaces office location"
              width="100%"
              height="100%"
              style={{ border: 0 }}
              loading="lazy"
              allowFullScreen
              referrerPolicy="strict-origin-when-cross-origin"
              className="h-full w-full"
            />
          </div>
        </div>

        {/* Closing section */}
        <div className="mt-6 rounded-2xl border border-indigo-100 bg-indigo-50/70 p-6 sm:mt-7 sm:rounded-3xl sm:p-8">
          <div className="flex flex-col gap-4 sm:flex-row sm:items-center sm:justify-between">
            <div className="flex items-start gap-3">
              <div className="flex h-10 w-10 shrink-0 items-center justify-center rounded-xl bg-white text-indigo-600 shadow-sm">
                <CheckCircle2 size={20} />
              </div>

              <div>
                <h3 className="font-['Poppins',sans-serif] text-lg font-semibold text-slate-950 sm:text-xl">
                  Every great space starts with a conversation.
                </h3>

                <p className="mt-2 max-w-2xl font-['DM_Sans',sans-serif] text-sm leading-6 text-slate-600">
                  Share your vision with Cube4Spaces & Paint Studio and take
                  the first step towards planning your space.
                </p>
              </div>
            </div>

            <a
              href="#contact"
              className="group inline-flex shrink-0 items-center justify-center gap-2 rounded-xl bg-slate-950 px-5 py-3 font-['DM_Sans',sans-serif] text-sm font-semibold text-white transition hover:bg-indigo-600"
            >
              Start Your Enquiry
              <Send
                size={15}
                className="transition-transform group-hover:translate-x-0.5"
              />
            </a>
          </div>
        </div>
      </div>
    </section>
  );
}